import type { RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

interface UseCarouselScrollOptions {
  scrollDistance?: number;
}

interface UseCarouselScrollResult {
  scrollContainerRef: RefObject<HTMLDivElement>;
  isAtStart: boolean;
  isAtEnd: boolean;
  scrollToPrev: () => void;
  scrollToNext: () => void;
}

/**
 * Custom hook for managing horizontal carousel scroll behavior.
 *
 * Features:
 * - Tracks scroll position to enable/disable navigation buttons
 * - Provides smooth scrolling handlers for prev/next navigation
 * - Automatically updates button states on scroll events
 *
 * @param options - Configuration options for carousel scroll
 * @returns Scroll container ref, button states, and navigation handlers
 */
export const useCarouselScroll = (options?: UseCarouselScrollOptions): UseCarouselScrollResult => {
  const { scrollDistance } = options ?? {};

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  // Update arrow button states based on scroll position
  const updateArrowStates = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setIsAtStart(scrollLeft <= 0);
    // Use -5 buffer for end detection (following HorizontalScrollingContainer pattern)
    setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 5);
  }, []);

  // Update arrow states on mount and when container or items change
  useEffect(() => {
    updateArrowStates();
  }, [updateArrowStates]);

  // Listen to scroll events to update arrow states
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Use passive listener for better performance (following HorizontalScrollingContainer pattern)
    container.addEventListener("scroll", updateArrowStates, { passive: true });
    return () => {
      container.removeEventListener("scroll", updateArrowStates);
    };
  }, [updateArrowStates]);

  const scrollToPrev = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const distance = scrollDistance ?? container.clientWidth;
    container.scrollBy({
      left: -distance,
      behavior: "smooth",
    });
  }, [scrollDistance]);

  const scrollToNext = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const distance = scrollDistance ?? container.clientWidth;
    container.scrollBy({
      left: distance,
      behavior: "smooth",
    });
  }, [scrollDistance]);

  return {
    scrollContainerRef,
    isAtStart,
    isAtEnd,
    scrollToPrev,
    scrollToNext,
  };
};
